import { useGetDashboardSummary, useGetUpdateHealth, useGetRecentActivity, useGetPipelineBreakdown } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { Building2, Users, FileText, Video, AlertTriangle, CheckCircle2, Clock, TrendingUp } from "lucide-react";
import InfoTip from "@/components/info-tip";

function StatCard({ title, value, icon: Icon, subtitle }: { title: string; value: number | string; icon: typeof Building2; subtitle?: string }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-serif font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="p-2 bg-muted rounded-md">
            <Icon className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function HealthBadge({ status }: { status: string }) {
  if (status === "healthy") return <Badge variant="default" className="bg-emerald-600 text-white text-xs" data-testid={`badge-health-${status}`}><CheckCircle2 className="w-3 h-3 mr-1" /> Healthy</Badge>;
  if (status === "warning") return <Badge variant="secondary" className="bg-amber-500 text-white text-xs" data-testid={`badge-health-${status}`}><Clock className="w-3 h-3 mr-1" /> Warning</Badge>;
  return <Badge variant="destructive" className="text-xs" data-testid={`badge-health-${status}`}><AlertTriangle className="w-3 h-3 mr-1" /> Overdue</Badge>;
}

export default function CommandCenter({ cohortSlug }: { cohortSlug?: string } = {}) {
  const cohortParams = cohortSlug ? { cohortSlug } : undefined;
  const { data: summary, isLoading: summaryLoading } = useGetDashboardSummary(cohortParams);
  const { data: health, isLoading: healthLoading } = useGetUpdateHealth(cohortParams);
  const { data: activity, isLoading: activityLoading } = useGetRecentActivity({ limit: 10, ...(cohortSlug ? { cohortSlug } : {}) });
  const { data: pipeline, isLoading: pipelineLoading } = useGetPipelineBreakdown(cohortParams);

  return (
    <div className="space-y-8" data-testid="page-command-center">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-serif font-bold tracking-tight">Command Center</h1>
          <InfoTip content="Your operational headquarters. This dashboard gives you a bird's-eye view of every company in the portfolio — their stage, update health, and recent activity — so you can spot issues before they become problems." />
        </div>
        <p className="text-sm text-muted-foreground mt-1">Portfolio overview and operational intelligence</p>
      </div>

      {summaryLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard title="Companies" value={summary.totalCompanies} icon={Building2} subtitle={`${summary.activeCompanies} active`} />
          <StatCard title="Pipeline" value={summary.pipelineCompanies} icon={TrendingUp} />
          <StatCard title="Founders" value={summary.totalFounders} icon={Users} />
          <StatCard title="Documents" value={summary.totalDocuments} icon={FileText} />
          <StatCard title="Media Assets" value={summary.totalMediaAssets} icon={Video} />
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">Pipeline Stages <InfoTip content="Shows where each company sits in the venture building journey — from early Pipeline (being evaluated) through Onboarding, Active (in the portfolio), to Exited." /></CardTitle>
          </CardHeader>
          <CardContent>
            {pipelineLoading ? (
              <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
            ) : pipeline ? (
              <div className="space-y-3">
                {pipeline.map((stage) => (
                  <div key={stage.stage} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div>
                      <p className="text-sm font-medium capitalize">{stage.stage}</p>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {stage.companies.map((c) => (
                          <Link key={c.id} href={cohortSlug ? `/c/${cohortSlug}/companies/${c.id}` : `/companies/${c.id}`}>
                            <Badge variant="outline" className="text-xs cursor-pointer hover:bg-accent" data-testid={`badge-company-${c.id}`}>{c.name}</Badge>
                          </Link>
                        ))}
                      </div>
                    </div>
                    <span className="text-lg font-serif font-bold text-muted-foreground">{stage.count}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">Update Health <InfoTip content="Tracks whether each company is sending their weekly check-ins on time. Healthy = on schedule, Warning = late, Overdue = missed their update window. Companies that go dark need attention." /></CardTitle>
          </CardHeader>
          <CardContent>
            {healthLoading ? (
              <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
            ) : health && health.length > 0 ? (
              <div className="space-y-3">
                {health.map((item) => (
                  <div key={item.companyId} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div>
                      <Link href={cohortSlug ? `/c/${cohortSlug}/companies/${item.companyId}` : `/companies/${item.companyId}`}>
                        <p className="text-sm font-medium hover:underline cursor-pointer" data-testid={`link-company-health-${item.companyId}`}>{item.companyName}</p>
                      </Link>
                      <p className="text-xs text-muted-foreground">{item.updateCount} updates total</p>
                    </div>
                    <HealthBadge status={item.status} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No active companies to track</p>
            )}

            {summary && summary.pendingReviews > 0 && (
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-md border border-amber-200 dark:border-amber-800">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{summary.pendingReviews} update{summary.pendingReviews !== 1 ? "s" : ""} pending review</p>
                <Link href={cohortSlug ? `/c/${cohortSlug}/updates` : "/updates"}>
                  <p className="text-xs text-amber-600 dark:text-amber-400 hover:underline mt-1 cursor-pointer" data-testid="link-pending-reviews">Review now</p>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">Recent Activity <InfoTip content="A live feed of everything happening across the portfolio — new updates, documents uploaded, investor views, approvals, and more. Think of it as your audit trail in real time." /></CardTitle>
          </CardHeader>
          <CardContent>
            {activityLoading ? (
              <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
            ) : activity && activity.length > 0 ? (
              <div className="space-y-2">
                {activity.map((item) => (
                  <div key={item.id} className="py-2 border-b border-border last:border-0" data-testid={`activity-item-${item.id}`}>
                    <div className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{item.description || item.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {item.companyName && <span className="text-xs text-muted-foreground">{item.companyName}</span>}
                          <span className="text-xs text-muted-foreground/60">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No recent activity</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
